import { SLA_DUE_FRACTION, slaState, type SupportSlaState } from '@zuruflex/core';
import { intlTagFor, toLocale, type Locale } from '@zuruflex/i18n';
import type { MessageThread } from '@zuruflex/types';
import { CheckCircle2, Clock, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Badge } from '../ui/badge.js';

/**
 * Where a clock stands against its target — `@zuruflex/core`'s verdict, not a
 * second opinion.
 *
 * This used to carry its own three states and its own threshold, and the
 * inbox rendered both on one row: it gated the badge's *visibility* on
 * `slaState` and then drew the badge from the component's own reading, so a
 * thread the "Past target" filter and the row gate both called `ok` wore an
 * amber "Due" pill — because anything still running was `due` here from the
 * first minute. Now there is one rule: `paused` draws nothing, the amber
 * band starts at `SLA_DUE_FRACTION` of the target, and the words are the
 * same words.
 */
export type SlaState = SupportSlaState;

export interface SlaLabels {
  firstResponse: string;
  resolution: string;
  /** Inside the target — a stopped clock that made it, or a running one with room. */
  ok: string;
  due: string;
  breached: string;
}

/** Only what a clock reads from — so a metrics row can be passed in too. */
export type SlaThread = Pick<
  MessageThread,
  'createdAt' | 'firstResponseAt' | 'resolvedAt' | 'status' | 'kind'
>;

export interface SlaIndicatorProps {
  thread: SlaThread;
  /** `messaging.firstResponseTargetMinutes`. */
  firstResponseTargetMinutes: number;
  /** `messaging.resolutionTargetHours`. */
  resolutionTargetHours: number;
  labels: SlaLabels;
  /** Draw one clock rather than both — the inbox row only has room for one. */
  only?: 'firstResponse' | 'resolution';
  /**
   * The instant to measure against. Defaults to render time; a caller with
   * a ticking clock of its own passes that, so a list of forty rows agrees
   * with itself rather than each row reading its own `Date.now()`.
   */
  now?: Date;
  className?: string;
}

const MINUTE = 60_000;

/** A `paused` thread draws nothing at all, so no clock ever carries that state. */
type RunningState = Exclude<SupportSlaState, 'paused'>;

interface Clock {
  key: 'firstResponse' | 'resolution';
  state: RunningState;
  /** Minutes on the clock: to the answer, or to now if there is none yet. */
  minutes: number;
}

/**
 * One clock's figure and colour.
 *
 * `SLA_DUE_FRACTION` rather than a threshold of this file's own: amber is
 * the last fifth of the target everywhere, which on the default
 * hour-to-first-reply is twelve minutes of warning. A clock that has
 * already stopped is never amber — it either made it or it did not, and
 * there is nothing left for an agent to save.
 */
function clockFor(
  key: Clock['key'],
  from: string,
  stoppedAt: string | null,
  targetMinutes: number,
  now: Date,
): Clock {
  const start = new Date(from).getTime();
  const end = stoppedAt ? new Date(stoppedAt).getTime() : now.getTime();
  const elapsed = Math.max(0, end - start);
  const minutes = Math.round(elapsed / MINUTE);
  const target = targetMinutes * MINUTE;

  // Strictly past, so a clock exactly at its target has not breached — the
  // same boundary `slaState` draws, and for the same reason.
  const state: RunningState =
    elapsed > target ? 'breached' : !stoppedAt && elapsed >= target * SLA_DUE_FRACTION ? 'due' : 'ok';

  return { key, state, minutes };
}

/**
 * `45 min`, `3 hr 10 min`, `2 days` — in the reader's language.
 *
 * Through `Intl` rather than through message keys, because the unit names
 * are the one part of this that every locale file would otherwise have to
 * carry three times over, and `Intl` already knows them.
 */
function formatMinutes(minutes: number, locale: Locale): string {
  const tag = intlTagFor(locale);
  const unit = (value: number, name: 'minute' | 'hour' | 'day') =>
    new Intl.NumberFormat(tag, { style: 'unit', unit: name, unitDisplay: 'short' }).format(value);

  if (minutes < 60) return unit(minutes, 'minute');
  if (minutes < 60 * 48) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${unit(hours, 'hour')} ${unit(rest, 'minute')}` : unit(hours, 'hour');
  }
  return unit(Math.round(minutes / (60 * 24)), 'day');
}

const TONE: Record<RunningState, { variant: 'success' | 'warning' | 'danger'; icon: typeof Clock }> =
  {
    ok: { variant: 'success', icon: CheckCircle2 },
    due: { variant: 'warning', icon: Clock },
    breached: { variant: 'danger', icon: TriangleAlert },
  };

/**
 * The two clocks a support thread is judged on.
 *
 * First response and resolution are reported on separately and are shown
 * separately, because they fail for different reasons: the first is a
 * staffing problem and the second is usually somebody else's. A thread
 * that was answered in four minutes and is still open three days later
 * should read as exactly that, not average into one amber pill.
 *
 * Both run from `createdAt`: somebody has been waiting since they wrote,
 * not since we got round to them, and restarting the clock at the first
 * reply would let a desk manufacture a good resolution figure by answering
 * "looking into it" quickly.
 *
 * **Nothing is drawn on a paused thread**, and the pause is `slaState`'s
 * call rather than each caller's: snoozed, resolved, closed, `DIRECT`, or
 * an operator whose targets failed to load. A snooze is an agreement that
 * there is nothing to do until Thursday and a resolved thread has stopped,
 * so leaving either accumulating minutes gives an inbox a permanent band of
 * red that means nothing happened rather than something is wrong.
 */
export function SlaIndicator({
  thread,
  firstResponseTargetMinutes,
  resolutionTargetHours,
  labels,
  only,
  now,
  className,
}: SlaIndicatorProps) {
  const { i18n } = useTranslation('common');
  const locale = toLocale(i18n.language);
  const at = now ?? new Date();

  const targets = { firstResponseTargetMinutes, resolutionTargetHours };
  // The gate, here rather than remembered independently at each call site.
  if (slaState(thread, targets, at) === 'paused') return null;

  const clocks: Clock[] = [
    clockFor('firstResponse', thread.createdAt, thread.firstResponseAt, firstResponseTargetMinutes, at),
    clockFor('resolution', thread.createdAt, thread.resolvedAt, resolutionTargetHours * 60, at),
  ].filter((clock) => !only || clock.key === only);

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {clocks.map((clock) => {
        const { variant, icon: Icon } = TONE[clock.state];
        return (
          <Badge
            key={clock.key}
            variant={variant}
            // The state in words as well as in colour: the difference
            // between the amber and the red pill is invisible to a good
            // share of the people reading an inbox all day.
            title={`${labels[clock.key]} · ${labels[clock.state]}`}
          >
            <Icon aria-hidden />
            <span className="sr-only">
              {labels[clock.key]} — {labels[clock.state]}:{' '}
            </span>
            <span className="tabular-nums">{formatMinutes(clock.minutes, locale)}</span>
          </Badge>
        );
      })}
    </div>
  );
}
